import { NextSeo } from 'next-seo';
import React, { FunctionComponent, useRef } from 'react';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import ConditionalHeaderRenderer from '@/components/common/ConditionalHeaderRenderer';
import AppHeader from '@/components/molecules/AppHeader';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import ShareSelectModal, {
  KAKAO_SHARE_TYPE,
} from '@/components/molecules/Modal/SelectModal/ShareSelectModal';
import PullToRefreshContainer from '@/components/molecules/PullToRefresh';
import { REPORT_TYPE } from '@/components/templates/mobile/communication/Common/ReportTemplate';
import useModal from '@/hooks/useModal';
import usePostReportModal from '@/hooks/usePostReportModal';
import useToast from '@/hooks/useToast';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import {
  historyBack,
  isMobile,
  isWebView,
  isWebViewByUserAgent,
} from '@/utils/mobile/RNPlugins';

import DeletedPostItem from './DeletedPostItem';
import PostCommentInput from './PostCommentInput';
import PostComments from './PostComments';
import PostContent from './PostContent';
import PostDetailSkeleton from './PostDetailSkeleton';
import useBoardPostDetailViewModel from './useBoardPostDetailViewModel';

interface Props {
  boardPostPid: string;
  userAgent: string;
}

const PostDetailTemplate: FunctionComponent<Props> = ({
  boardPostPid,
  userAgent,
}) => {
  const { showModal, closeModal } = useModal();
  const { showToast } = useToast();
  const {
    showReportModal,
    onShowReportModal,
    onCloseReportModal,
    reportData,
    reportItems,
  } = usePostReportModal();
  const viewModel = useBoardPostDetailViewModel({
    boardPostPid,
  });

  const commentsRef = useRef<HTMLUListElement>(null);

  const updateLikePostStatus = (isLike: boolean) => {
    if (isLike) {
      viewModel.updatePostLikeStatus();
    } else {
      showToast({ message: '이미 공감한 게시글이에요.' });
    }
  };

  const setLikeCommentStatus = (
    e: React.MouseEvent,
    boardCommentPid: string,
    isLike: boolean,
  ) => {
    e.stopPropagation();
    if (isLike) {
      viewModel.updateCommentLikeStatus(boardCommentPid);
    } else {
      showToast({ message: '이미 공감한 댓글이에요.' });
    }
  };

  const onScrollBottom = () => {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }, 500);
  };

  const handleAddCommentByPost = async (content: string) => {
    if (!viewModel.addCommentLoading) {
      const success = await viewModel.onSubmitCommentByPost(content);
      if (success && isWebView()) {
        onScrollBottom();
      }
    }
  };

  // 질문 신고하기
  const onReport = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!viewModel.isLoggedIn) {
      return viewModel.onOpenAuthModal();
    }

    if (viewModel.boardPostDetail) {
      onShowReportModal({
        targetId: viewModel.boardPostDetail.boardPostPid,
        targetType: REPORT_TYPE.BOARD_POST,
        isMine: viewModel.boardPostDetail.isMine,
        isDetailPage: true,
        isAdmin: viewModel.userProfile?.isAdmin,
        attachmentTargetId:
          viewModel.boardPostDetail.boardPostAttachments[0]
            ?.boardPostAttachmentId ?? undefined,
      });
    }
  };

  const onChangeShowShareModal = () => {
    viewModel.onToggleShareModal(!viewModel.isShareModalOpen);
  };

  const onShowBannedUserModal = () => {
    showModal({
      type: MODAL_TYPE.ALERT,
      props: {
        title: '커뮤니티 정책을 위반했어요.',
        message: `제한 기간 동안 게시글과 댓글을 쓸 수 없어요.\n(제한 기간 : ${viewModel.userProfile?.formattedBannedDate})`,
        buttons: [
          {
            type: MODAL_BUTTON_TYPE.DEFAULT,
            label: '확인',
            onClick: closeModal,
          },
        ],
      },
    });
  };

  const onClickInput = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!viewModel.isLoggedIn) {
      return viewModel.onOpenAuthModal();
    }
    if (viewModel.userProfile?.isBanned) {
      onShowBannedUserModal();
    }
  };

  if (viewModel.isBoardPostDetailLoading) return <PostDetailSkeleton />;
  if (!viewModel.boardPostDetail) return <></>;
  if (viewModel.boardPostDetail.blinded)
    return (
      <DeletedPostItem title={viewModel.boardPostDetail.boardCategory.name} />
    );

  return (
    <>
      <NextSeo
        title={`${viewModel.boardPostDetail.title} - 닥터나우`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.COMMUNITY.DETAIL.path}/${viewModel.boardPostDetail.boardPostPid}`}
        description={`${viewModel.boardPostDetail.content}`}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.COMMUNITY.DETAIL.path}/${viewModel.boardPostDetail.boardPostPid}`,
          title: `${viewModel.boardPostDetail.title} - 닥터나우`,
          description: `${viewModel.boardPostDetail.content}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_COMMUNITY_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        twitter={{
          handle: '@doctornow',
          site: '@doctornow.co.kr',
          cardType: 'summary_large_image',
        }}
      />
      <ConditionalHeaderRenderer
        isWebView={isWebViewByUserAgent(userAgent)}
        appHeader={
          <AppHeader
            onClickBackButton={historyBack}
            title={viewModel.boardPostDetail.boardCategory.name}
          />
        }
        paddingTop={0}
      >
        {!isMobile(userAgent) && (
          <BreadCrumbsWrapper>
            <BreadCrumbs
              visible
              path={ROUTES.COMMUNITY.LIST.path}
              type="community"
              name={viewModel.boardPostDetail.boardCategory.name}
              urlParameter={`?categoryId=${viewModel.boardPostDetail.boardCategoryId}`}
            />
          </BreadCrumbsWrapper>
        )}
        <Container isWebview={isWebViewByUserAgent(userAgent)}>
          <PullToRefreshContainer
            onRefresh={viewModel.boardPostRefresh}
            pullingContent={''}
          >
            <>
              <PostContent
                boardPostDetail={viewModel.boardPostDetail}
                onReport={onReport}
                updateLikePostStatus={updateLikePostStatus}
                onShowShareModal={onChangeShowShareModal}
              />
              {!!viewModel.boardPostDetail.boardComments.length && (
                <PostComments
                  commentsRef={commentsRef}
                  boardPostDetail={viewModel.boardPostDetail}
                  refreshPost={viewModel.boardPostRefresh}
                  setLikeCommentStatus={setLikeCommentStatus}
                  userProfile={viewModel.userProfile}
                  onShowBannedUserModal={onShowBannedUserModal}
                />
              )}
            </>
          </PullToRefreshContainer>

          <PostCommentInput
            isLoggedIn={viewModel.isLoggedIn}
            userProfile={viewModel.userProfile}
            comment={viewModel.comment}
            onChangeInput={viewModel.onChangeCommentInput}
            onSubmit={handleAddCommentByPost}
            onClickInput={onClickInput}
          />

          {reportItems && (
            <ReportSelectModal
              isVisible={showReportModal}
              onClose={onCloseReportModal}
              selectList={reportItems}
              title={
                reportData?.isAdmin
                  ? '관리 하기'
                  : reportData?.isMine
                  ? '게시글 관리'
                  : '신고하기'
              }
            />
          )}

          {/* 공유하기 카카오톡/주소복사 */}
          <ShareSelectModal
            isVisible={viewModel.isShareModalOpen}
            onClose={onChangeShowShareModal}
            type={KAKAO_SHARE_TYPE.BOARD_POST}
            pageName={viewModel.pageName}
            kakaoShare={{
              id: viewModel.boardPostDetail.boardPostPid,
              title: viewModel.boardPostDetail.title,
              content: viewModel.boardPostDetail.content,
            }}
          />
        </Container>
      </ConditionalHeaderRenderer>
    </>
  );
};

export default PostDetailTemplate;

const Container = styled.div<{ isWebview: boolean }>`
  ${({ theme, isWebview }) => `
    width: 100%;
    height: auto;
    min-height: ${isWebview ? '100vh' : '0'};
    background: ${theme.color.gray30};
  `};

  position: relative;
`;

const BreadCrumbsWrapper = styled.div`
  padding: 26px 24px 0 24px;
`;
